import { useSyncExternalStore } from 'react';

/**
 * False while the markup on screen is still the server's, true once React owns it.
 *
 * **This is the escape hatch for the handful of things that legitimately cannot render the same on
 * both sides**, and it is deliberately narrow. The content pages are rendered at build time, where
 * there is no visitor: no session, no remembered cookie decision, no chosen appearance. A component
 * that reads one of those and renders it straight away produces markup the build cannot have
 * written, and React replaces the whole tree rather than patching it.
 *
 * The usual answer is to give the reader a server snapshot and let the value settle after
 * hydration — which is what `useSession` and `useTheme` do, because a heading that changes from
 * "Sign in" to "Portal" is a correction the visitor can live with. This hook is for the cases where
 * the settled state is *absence*: a cookie banner that appears for a tenth of a second and then
 * removes itself has asked a question nobody could answer, and teaches people to ignore the next
 * one.
 *
 * `useSyncExternalStore` rather than a `useState` and an effect, because the subscription never
 * fires: the server snapshot is false, the client snapshot is true, and React reads the first
 * during hydration and the second immediately after. There is no second render to schedule and no
 * flag to keep.
 */
export function useHydrated(): boolean {
    return useSyncExternalStore(subscribe, clientSnapshot, serverSnapshot);
}

/** Nothing ever changes, so nothing is ever notified. */
function subscribe(): () => void {
    return () => undefined;
}

function clientSnapshot(): boolean {
    return true;
}

function serverSnapshot(): boolean {
    return false;
}
